<template>
  <div :class="selected_report?.story_board || print_mode ? 'px-print' : 'container'">
    <StatementOfUse
      :class="selected_report?.story_board ? '' : 'mt-5'"
      :table_header_color="table_header_color"
      :table_style="table_style"
      :table_header_font_color="table_header_font_color"
    />

    <GriSbLinkedTable
      v-if="selected_report?.story_board"
      :print_mode="print_mode"
      :table_header_color="table_header_color"
      :table_style="table_style"
      :table_header_font_color="table_header_font_color"
    />

    <template v-else>
      <!-- Universal Disclosure -->
      <p>Universal Standards</p>
      <GriTableSkeleton v-if="isLoading" :print_mode="print_mode" />
      <template v-else>
        <div
          v-for="(category, index) in report_universal_categories"
          :key="category.category_id"
          :class="{ 'first-subtopic': index === 0, 'new-page-subtopic': index > 0 }"
        >
          <template
            v-if="
              hash_rp === null ||
              (hash_rp == category.category_id &&
                getUniqueSubtopicsByCategoryIds(category.category_id).length > 0)
            "
          >
            <h3 :id="!print_mode ? 'rp' + category.category_id : ''" class="pb-3">
              GRI {{ category.category_id }}.
              {{ lang != 'ENG' ? category.category_name : category.category_name_e }}
            </h3>

            <b-row>
              <b-col cols="3">
                {{ lang != 'ENG' ? '보고 지표' : 'Disclosures' }}
                <HeaderAnchor :active_code="category.category_id" :print_mode="print_mode" />
              </b-col>
              <b-col cols="3">
                {{ lang != 'ENG' ? '보고 내용' : 'Disclosure Contents' }}
              </b-col>
              <b-col cols="6" class="text-right">
                <b-icon icon="table" class="mr-1" />{{
                  lang != 'ENG' ? '데이터 테이블' : 'Data Tables'
                }}
                <b-icon icon="file-earmark-text" class="ml-3 mr-1" />{{
                  lang != 'ENG' ? '문서' : 'Files'
                }}
                <b-icon icon="link-45deg" class="ml-3 mr-1" />{{
                  lang != 'ENG' ? '외부 링크' : 'URLs'
                }}
                <b-icon icon="briefcase" class="ml-3 mr-1" />{{
                  lang != 'ENG' ? '비즈니스 사례' : 'Business Cases'
                }}
              </b-col>
            </b-row>
          </template>

          <div
            v-for="(subtopic, index) in getUniqueSubtopicsByCategoryIds(category.category_id)"
            :key="subtopic.subtopic_code"
            :class="{ 'first-subtopic': index === 0, 'new-page-subtopic': index > 0 }"
          >
            <div class="mb-5 border-0">
              <template>
                <div
                  v-if="
                    hash_crp === null ||
                    hash_crp == subtopic.subtopic_code ||
                    subtopic.subtopic_code.split('_')[0] == hash_trp
                  "
                  class="d-flex"
                  @click="parse_hash(null, null, subtopic.subtopic_code)"
                >
                  <div class="col-12 py-1" :style="main_color_sub">
                    <span
                      :id="!print_mode ? 'rp' + subtopic.subtopic_code : ''"
                      class="fw-600 f-140"
                    >
                      {{ index + 1 }}.
                      {{ lang != 'ENG' ? subtopic.subtopic_name : subtopic.subtopic_name_e }}
                    </span>
                    <span class="gray-500 f-90 fw-400 ml-1">
                      {{ subtopic.topic_publication_date }}
                    </span>
                  </div>
                </div>

                <div
                  v-for="disclosure in getGRIContentsBySubtopicCodes(subtopic.subtopic_code)"
                  :id="!print_mode ? 'w' + disclosure.gri_code : ''"
                  :key="disclosure.disclosure_id"
                >
                  <div
                    v-if="shouldRenderDisclosure(disclosure, subtopic)"
                    class="d-flex border-bottom py-3"
                  >
                    <div class="col-3">
                      <div class="fw-500">{{ disclosure.gri_code }}</div>
                      <p :id="!print_mode ? 'crp' + disclosure.gri_code : ''">
                        {{
                          lang != 'ENG' ? disclosure.indicator_name : disclosure.indicator_name_e
                        }}
                      </p>
                    </div>

                    <div v-if="!disclosure.nan_category" class="col-9">
                      <p
                        v-if="lang === 'KOR' && disclosure.direct_report"
                        class="page-no-break-inside"
                        style="white-space: pre-line"
                      >
                        <span v-html="disclosure.direct_report"></span>
                      </p>
                      <p
                        v-else-if="lang === 'ENG' && disclosure.direct_report_e"
                        class="page-no-break-inside"
                        style="white-space: pre-line"
                      >
                        <span v-html="disclosure.direct_report_e"></span>
                      </p>

                      <!-- data_tables 넣을자리 -->
                      <div
                        v-for="(data_table_id, data_table_idx) in disclosure.data_tables"
                        :key="'data_table_' + data_table_id + data_table_idx"
                      >
                        <DataTable
                          :print_mode="print_mode"
                          :table_mode="
                            print_mode && table_mode === 'popup' ? 'accordion' : table_mode
                          "
                          :data_table_id="data_table_id"
                          :modal_table="modal_table"
                          :lazy_load="isMobile"
                          :table_style="table_style"
                          :show_table_line="show_table_line"
                          :show_table_outline="show_table_outline"
                          :table_header_color="table_header_color"
                          :table_header_font_color="table_header_font_color"
                        />
                      </div>

                      <!-- documents 넣을자리 -->
                      <div class="d-flex flex-column mb-2 mt-2">
                        <span
                          v-for="document_id in disclosure.documents"
                          :key="'document_' + document_id"
                        >
                          <Document
                            :document_id="document_id"
                            :lazy_load="false"
                            :print_mode="print_mode"
                          />
                        </span>
                      </div>

                      <!-- business_cases 넣을자리 -->
                      <div class="d-flex flex-column mb-2 mt-2">
                        <span
                          v-for="business_case_id in disclosure.business_cases"
                          :key="'business_case_' + business_case_id"
                        >
                          <BusinessCase
                            :business_case_id="business_case_id"
                            :lazy_load="isMobile"
                            :business_case_mode="
                              print_mode && business_case_mode === 'popup'
                                ? 'accordion'
                                : business_case_mode
                            "
                          />
                        </span>
                      </div>

                      <div v-for="chart_id in disclosure.charts" :key="'chart_' + chart_id">
                        <ChartCanvas
                          v-if="chart_id !== ''"
                          :key="`chart_${chart_id}`"
                          :print_mode="print_mode"
                          :chart_id="chart_id"
                          :viewer_mode="true"
                          class="mb-5"
                          :lazy_load="isMobile"
                        />
                      </div>

                      <div
                        v-if="
                          disclosure.gri_code === '3-2' &&
                          selected_indicator &&
                          selected_indicator.custom_topic_indicator.length > 0
                        "
                        class="mt-5"
                      >
                        <b-table-simple
                          responsive
                          :class="
                            `${table_style}` === 'basic_table' ? 'edi-table' : 'no-line-table'
                          "
                          :table-class="
                            `${table_style}` === 'basic_table' ? 'edi-table' : 'no-line-table'
                          "
                          :borderless="!show_table_line"
                          :outlined="show_table_outline"
                        >
                          <b-thead>
                            <b-tr>
                              <b-td
                                :style="
                                  `${table_style}` === 'custom_table'
                                    ? `color: ${table_header_font_color}; background-color: ${table_header_color}`
                                    : ''
                                "
                              >
                                {{ lang !== 'ENG' ? '우선순위' : 'Ranking' }}
                              </b-td>
                              <b-td
                                :style="
                                  `${table_style}` === 'custom_table'
                                    ? `color: ${table_header_font_color}; background-color: ${table_header_color}`
                                    : ''
                                "
                              >
                                {{ lang !== 'ENG' ? '중요 주제명' : 'Topic' }}
                              </b-td>
                              <b-td
                                :style="
                                  `${table_style}` === 'custom_table'
                                    ? `color: ${table_header_font_color}; background-color: ${table_header_color}`
                                    : ''
                                "
                              >
                                {{
                                  lang !== 'ENG'
                                    ? '중요 주제와 연관된 GRI 지표'
                                    : 'GRI Topic Standards'
                                }}
                              </b-td>
                            </b-tr>
                          </b-thead>
                          <b-tbody>
                            <b-tr
                              v-for="(item, idx) in selected_indicator.custom_topic_indicator"
                              :key="idx"
                            >
                              <b-td>
                                {{ idx + 1 }}
                              </b-td>
                              <b-td>
                                {{
                                  lang !== 'ENG'
                                    ? item.topic_name
                                    : item.topic_name_em
                                    ? item.topic_name_em
                                    : item.topic_name_ea
                                }}
                              </b-td>
                              <b-td>
                                <span
                                  v-for="gri_code in sort_indicator_arr(
                                    item.selected_cm_gri_code_list
                                  )"
                                  :key="gri_code"
                                >
                                  <a href="#" @click="navAnchor($event, gri_code)">
                                    {{ gri_code }} </a
                                  >&nbsp;
                                </span>

                                <span
                                  v-if="
                                    item.selected_cm_gri_code_list &&
                                    item.selected_cm_gri_code_list.length === 0
                                  "
                                >
                                  Non-GRI
                                </span>
                              </b-td>
                              <!-- <template v-for="property in hashed_data_tables[dt].header"> -->

                              <!-- <b-td v-if="differentPrevious(hashed_data_tables[dt].contents, row_index, property, hashed_data_tables[dt].header)==true" :key="property.id" :rowspan="getSameLength(hashed_data_tables[dt].contents, row_index, property, hashed_data_tables[dt].header)">
                                                                    {{ row_item[property] }}
                                                                </b-td> -->
                              <!-- </template> -->
                            </b-tr>
                          </b-tbody>
                        </b-table-simple>
                      </div>
                    </div>
                    <div v-else class="col-9">
                      <div>
                        <p>{{ lang !== 'ENG' ? '보고 생략' : 'Omission in report' }}</p>
                        <p>
                          {{ lang !== 'ENG' ? '사유' : 'Reason' }}: [{{
                            lang !== 'ENG'
                              ? nan_table[disclosure.nan_category]['KOR']
                              : nan_table[disclosure.nan_category]['ENG']
                          }}]
                          {{
                            lang !== 'ENG'
                              ? disclosure.nan_reason_contents
                              : disclosure.nan_reason_contents_em
                              ? disclosure.nan_reason_contents_em
                              : disclosure.nan_reason_contents_ea
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <!-- GRI 200 ~ 400 -->
      <p class="new-page">Topic Standards Disclosures</p>
      <GriTableSkeleton v-if="isLoading" :print_mode="print_mode" />
      <template v-else>
        <div
          v-for="(category, index) in report_topic_specific_categories"
          :key="category.category_id"
          :class="{ 'first-subtopic': index === 0, 'new-page-subtopic': index > 0 }"
        >
          <div
            v-if="
              (hash_rp === null || hash_rp == category.category_id) &&
              getGRIContentsByCategoryIds(category.category_id).length > 0
            "
          >
            <h3 :id="!print_mode ? 'rp' + category.category_id : ''" class="pb-3">
              GRI Topic. {{ lang != 'ENG' ? category.category_name : category.category_name_e }}
            </h3>

            <b-row>
              <b-col cols="3">
                {{ lang != 'ENG' ? '보고 지표' : 'Disclosures' }}
                <HeaderAnchor :active_code="category.category_id" :print_mode="print_mode" />
              </b-col>
              <b-col cols="3">
                {{ lang != 'ENG' ? '보고 내용' : 'Disclosure Contents' }}
              </b-col>
              <b-col cols="6" class="text-right">
                <b-icon icon="table" class="mr-1" />{{
                  lang != 'ENG' ? '데이터 테이블' : 'Data Tables'
                }}
                <b-icon icon="file-earmark-text" class="ml-3 mr-1" />{{
                  lang != 'ENG' ? '문서' : 'Files'
                }}
                <b-icon icon="link-45deg" class="ml-3 mr-1" />{{
                  lang != 'ENG' ? '외부 링크' : 'URLs'
                }}
                <b-icon icon="briefcase" class="ml-3 mr-1" />{{
                  lang != 'ENG' ? '비즈니스 사례' : 'Business Cases'
                }}
              </b-col>
            </b-row>

            <template>
              <div
                v-for="(topic, index) in getUniqueTopicsByCategoryIds(category.category_id)"
                :key="topic.topic_id"
                :class="{ 'first-subtopic': index === 0, 'new-page-subtopic': index > 0 }"
              >
                <div v-if="hash_trp === null || hash_trp == topic.topic_id" class="mb-5 border-0">
                  <div class="d-flex">
                    <div class="col-12 py-1" :style="main_color_sub">
                      <span :id="!print_mode ? 'trp' + topic.topic_id : ''" class="fw-600 f-140">
                        GRI {{ topic.topic_id }} :
                        {{ lang != 'ENG' ? topic.topic_name : topic.topic_name_e }}
                      </span>
                      <span class="gray-500 f-90 fw-400 ml-1">{{
                        topic.topic_publication_date
                      }}</span>
                    </div>
                  </div>

                  <template>
                    <div
                      v-for="disclosure in getGRIContentsByTopicIds(topic.topic_id)"
                      :id="!print_mode ? 'w' + disclosure.gri_code : ''"
                      :key="disclosure.disclosure_id"
                    >
                      <div
                        v-if="shouldRenderDisclosure(disclosure)"
                        class="d-flex border-bottom py-3"
                      >
                        <div class="col-3">
                          <div class="fw-500">{{ disclosure.gri_code }}</div>
                          <p :id="!print_mode ? 'crp' + disclosure.gri_code : ''">
                            {{
                              lang != 'ENG'
                                ? disclosure.indicator_name
                                : disclosure.indicator_name_e
                            }}
                          </p>
                        </div>
                        <div v-if="!disclosure.nan_category" class="col-9">
                          <p
                            v-if="lang === 'KOR' && disclosure.direct_report"
                            class="page-no-break-inside"
                            style="white-space: pre-line"
                          >
                            <span v-html="disclosure.direct_report"></span>
                          </p>
                          <p
                            v-else-if="lang === 'ENG' && disclosure.direct_report_e"
                            class="page-no-break-inside"
                            style="white-space: pre-line"
                          >
                            <span v-html="disclosure.direct_report_e"></span>
                          </p>

                          <!-- data_tables 넣을자리 -->
                          <div
                            v-for="data_table_id in disclosure.data_tables"
                            :key="'data_table_' + data_table_id"
                          >
                            <DataTable
                              :print_mode="print_mode"
                              :table_mode="
                                print_mode && table_mode === 'popup' ? 'accordion' : table_mode
                              "
                              :data_table_id="data_table_id"
                              :modal_table="modal_table"
                              :lazy_load="isMobile"
                              :table_style="table_style"
                              :show_table_line="show_table_line"
                              :show_table_outline="show_table_outline"
                              :table_header_color="table_header_color"
                              :table_header_font_color="table_header_font_color"
                            />
                          </div>

                          <!-- documents 넣을자리 -->
                          <div class="d-flex flex-column mb-2 mt-2">
                            <span
                              v-for="document_id in disclosure.documents"
                              :key="'document_' + document_id"
                            >
                              <Document
                                :document_id="document_id"
                                :lazy_load="false"
                                :print_mode="print_mode"
                              />
                            </span>
                          </div>

                          <!-- business_cases 넣을자리 -->
                          <div class="d-flex flex-column mb-2 mt-2">
                            <span
                              v-for="business_case_id in disclosure.business_cases"
                              :key="'business_case_' + business_case_id"
                            >
                              <BusinessCase
                                :business_case_id="business_case_id"
                                :lazy_load="isMobile"
                                :business_case_mode="
                                  print_mode && business_case_mode === 'popup'
                                    ? 'accordion'
                                    : business_case_mode
                                "
                              />
                            </span>
                          </div>

                          <div v-for="chart_id in disclosure.charts" :key="'chart_' + chart_id">
                            <ChartCanvas
                              v-if="chart_id !== ''"
                              :key="`chart_${chart_id}`"
                              :print_mode="print_mode"
                              :chart_id="chart_id"
                              :viewer_mode="true"
                              class="mb-5"
                              :lazy_load="isMobile"
                            />
                          </div>
                        </div>
                        <div v-else class="col-9">
                          <div>
                            <p>{{ lang !== 'ENG' ? '보고 생략' : 'Omission in report' }}</p>
                            <p>
                              {{ lang !== 'ENG' ? '사유' : 'Reason' }}: [{{
                                lang !== 'ENG'
                                  ? nan_table[disclosure.nan_category]['KOR']
                                  : nan_table[disclosure.nan_category]['ENG']
                              }}]
                              {{
                                lang !== 'ENG'
                                  ? disclosure.nan_reason_contents
                                  : disclosure.nan_reason_contents_em
                                  ? disclosure.nan_reason_contents_em
                                  : disclosure.nan_reason_contents_ea
                              }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import DataTable from '@/components/report_preview/gri2021_viewer/viewer_components/DataTable.vue';
import Document from '@/components/report_preview/gri2021_viewer/viewer_components/Document.vue';
import BusinessCase from '@/components/report_preview/gri2021_viewer/viewer_components/BusinessCase.vue';
import HeaderAnchor from '@/components/report_preview/gri2021_viewer/viewer_components/HeaderAnchor';
import { nan_table } from '@/components/util';
import StatementOfUse from '@/components/report_preview/gri2021_viewer/viewer_components/StatementOfUse.vue';
import ChartCanvas from '@/components/writing_report/chart_subs/ChartCanvas.vue';
import GriSbLinkedTable from './GriSbLinkedTable.vue';
import GriTableSkeleton from './GRITableSkeleton.vue';
import { gri_mixin } from '@/components/writing_report/write_report_subs/gri_mixin.js';
import { sort_indicator_arr, navAnchor } from '../../../util';
export default {
  name: 'GRITable2021',
  components: {
    Document,
    DataTable,
    BusinessCase,
    HeaderAnchor,
    StatementOfUse,
    ChartCanvas,
    GriSbLinkedTable,
    GriTableSkeleton
  },
  mixins: [gri_mixin],
  props: {
    modal_table: { type: Boolean, default: false },
    reload: { type: Boolean, default: false },
    print_mode: { type: Boolean, default: false },
    business_case_mode: { type: String, default: 'popup' },
    table_mode: { type: String, default: 'accordion' },
    material_style_option: { type: String, default: 'outline' },

    table_style: { type: String, default: 'basic_table' },
    show_table_line: { type: Boolean, default: true },
    show_table_outline: { type: Boolean, default: false },
    table_header_color: { type: String, default: '#dedede' },
    table_header_font_color: { type: String, default: '#fffff' }
  },
  data() {
    return {
      show_data_table: {},
      show_business_case: {},
      low_height: false,
      selected_indicator: null,
      hash_rp: null,
      hash_trp: null,
      hash_crp: null,
      nan_table
    };
  },
  computed: {
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('gri2021_report_view', ['report_contents_loaded']),
    ...mapGetters('gri2021_report_view', [
      'report_universal_categories',
      'report_topic_specific_categories'
    ]),
    ...mapGetters(['isMobile']),
    ...mapGetters('report_list', ['current_report_code']),
    isLoading() {
      return (
        !this.report_universal_categories ||
        this.report_universal_categories.length === 0 ||
        !this.report_topic_specific_categories ||
        this.report_topic_specific_categories.length === 0
      );
    },
    main_color() {
      return `color:` + this.selected_report.main_color;
    },
    main_color_sub() {
      if (this.material_style_option === 'fill') {
        return `background-color: ${this.selected_report.main_color}; color: white; border: 1px solid white; `;
      } else if (this.material_style_option === 'outline') {
        return (
          `color:` +
          this.selected_report.main_color +
          `; border-top: 1px solid ` +
          this.selected_report.main_color +
          `!important` +
          `; border-bottom: 1px solid ` +
          this.selected_report.main_color
        );
      } else {
        return `background-color: white; border-left: 3px solid ${this.selected_report.main_color}; border-bottom: 1px solid ${this.selected_report.sub_color}; border-top: 0px; border-right: 0px;`;
      }
    },
    main_bg_color() {
      if (this.selected_report) {
        return `background-color:` + this.selected_report.main_color;
      } else return `background-color: #494949`;
    },

    /**
     * @description 공시 항목 렌더링 여부를 결정합니다.
     * 인자를 전달받기 위해 함수를 반환하는 형태로 작성되었습니다.
     * 해시 필터 조건과 콘텐츠 존재 조건을 모두 만족해야 렌더링됩니다.
     */
    shouldRenderDisclosure() {
      return (disclosure, context = null) => {
        // 1. URL 해시에 따른 필터링 조건
        let filterCondition = false;
        if (context && context.subtopic_code) {
          // Universal Standards의 경우
          filterCondition =
            this.hash_crp === null ||
            this.hash_crp === disclosure.gri_code ||
            this.hash_crp === context.subtopic_code;
        } else {
          // Topic Standards의 경우
          filterCondition = this.hash_crp === null || this.hash_crp === disclosure.gri_code;
        }

        // 2. 표시할 콘텐츠가 하나라도 있는지 확인하는 조건
        const directReportContent =
          this.lang !== 'ENG' ? disclosure.direct_report : disclosure.direct_report_e;
        const hasContent =
          !!directReportContent ||
          (disclosure.data_tables && disclosure.data_tables.length > 0) ||
          (disclosure.documents && disclosure.documents.length > 0) ||
          (disclosure.charts && disclosure.charts.length > 0) ||
          (disclosure.business_cases && disclosure.business_cases.length > 0);

        // 3. 보고 생략으로 처리되었는지 확인하는 조건
        const isOmitted = !!disclosure.nan_category; // 필터 조건을 만족하고, (콘텐츠가 있거나 또는 보고 생략 처리된 경우) true를 반환
        return filterCondition && (hasContent || isOmitted);
      };
    }
  },
  methods: {
    ...mapMutations('gri2021_report_view', ['update_report_contents_loaded']),
    sort_indicator_arr,
    navAnchor,
    parse_hash(crp = null, trp = null, rp = null) {
      if (crp !== null) {
        return;
      } else if (trp !== null) {
        return;
      } else if (rp !== null) {
        this.hash_crp = rp;
        this.hash_trp = rp[0];
        this.hash_rp = rp[0];
        return;
      }

      const page_hash = this.$route.hash;
      if (page_hash.includes('crp')) {
        this.hash_crp = page_hash.split('crp')[1];
        this.hash_trp = null;
        this.hash_rp = null;

        this.hash_trp = this.hash_crp.split('-')[0];
        if (this.hash_trp.length > 2) {
          this.hash_rp = this.hash_trp[0] + '00';
        } else {
          this.hash_rp = this.hash_trp[0];
        }
        if (this.hash_crp.includes('3-3_')) {
          this.hash_trp = this.hash_crp.split('3-3_')[1];
          this.hash_rp = this.hash_trp[0] + '00';
        }
      } else if (page_hash.includes('trp')) {
        this.hash_crp = null;
        this.hash_trp = page_hash.split('trp')[1];

        if (this.hash_trp.length > 2) {
          this.hash_rp = this.hash_trp[0] + '00';
        } else {
          this.hash_rp = this.hash_trp[0];
        }
      } else if (page_hash.includes('rp')) {
        this.hash_crp = null;
        this.hash_trp = null;
        this.hash_rp = page_hash.split('rp')[1];

        if (this.hash_rp.startsWith('2_') || this.hash_rp.startsWith('3_')) {
          this.hash_crp = this.hash_rp;
          this.hash_trp = this.hash_rp[0];
          this.hash_rp = this.hash_rp[0];
        }
      }
    },
    async selected_gri_code_list() {
      const response = await this.$publicApi.get(
        '/get_selected_gri_indicator_code?rc=' + this.current_report_code
      );
      if (response.data.code) {
        this.selected_indicator = response.data.result;
      }
    }
  },
  watch: {
    $route: {
      handler(to, from) {
        // this.parse_hash();
      }
    }
  },
  created() {
    if (this.reload == true) {
      this.update_report_contents_loaded(false);
    }

    this.parse_hash();
    this.selected_gri_code_list();
  }
};
</script>

<style>
@media print {
  .new-page {
    page-break-before: always;
  }

  .first-subtopic {
    page-break-before: avoid;
  }

  .new-page-subtopic {
    page-break-before: always;
  }
}
</style>
